import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { searchPatients } from '../../firebase';

interface PatientSearchModalProps {
  onClose: () => void;
  onSelect: (patient: any) => void;
  title?: string;
  placeholder?: string;
}

export function PatientSearchModal({ onClose, onSelect, title, placeholder }: PatientSearchModalProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async (term: string) => {
    // Check if it looks like a Case ID (starts with CASE-)
    const isCaseId = term.trim().toUpperCase().startsWith('CASE-');
    
    if (isCaseId && term.trim().length >= 6) {
      // If it's a Case ID, select it directly
      onSelect({ caseId: term.trim().toUpperCase(), id: term.trim().toUpperCase() });
      return;
    }

    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchPatients(term);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching patients:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="patient-search-modal-overlay" onClick={onClose}>
      <div className="patient-search-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="patient-search-modal-header">
          <h2>{title || t('events.selectPatient')}</h2>
          <button 
            onClick={onClose}
            className="close-modal-btn"
          >
            ✕
          </button>
        </div>
        <div className="patient-search-modal-body">
          <div className="search-input-container">
            <input
              type="text"
              placeholder={placeholder || t('formManager.searchOrCaseId') || 'Search by name, email, or enter Case ID (e.g., CASE-022)'}
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                handleSearch(value);
              }}
              className="patient-search-input"
              autoFocus
            />
            {isSearching && (
              <div className="search-loading">{t('events.searching')}</div>
            )}
          </div>
          <div className="patient-search-results">
            {searchResults.length > 0 ? (
              searchResults.map((patient) => (
                <div 
                  key={patient.caseId}
                  className="patient-search-result-item"
                  onClick={() => onSelect(patient)}
                >
                  <div className="patient-info">
                    <div className="patient-name">
                      {patient.firstName} {patient.lastName}
                    </div>
                    <div className="patient-details">
                      <span className="patient-email">{patient.email}</span>
                      <span className="patient-case-id">{patient.caseId}</span>
                    </div>
                  </div>
                  <div className="select-patient-btn">{t('events.select')}</div>
                </div>
              ))
            ) : searchTerm.length >= 2 && !isSearching ? (
              <div className="no-results">{t('events.noPatients')} "{searchTerm}"</div>
            ) : searchTerm.length < 2 ? (
              <div className="search-hint">{t('events.searchHint')}</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}


